import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, FileText, Wrench } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SearchInput } from "@/components/site/SearchInput";
import { matchesQuery } from "@/lib/search";
import api from "@/lib/api";

const ResultRow = ({ item, onPick }) => (
  <Link
    to={item.href}
    onClick={onPick}
    data-testid={`search-result-${item.kind}-${item.id}`}
    className="flex items-start gap-3 px-4 py-3 rounded-lg hover:bg-slate-800/60 transition-colors"
  >
    {item.kind === "article" ? (
      <FileText className="h-4 w-4 mt-1 text-sky-400 flex-shrink-0" />
    ) : (
      <Wrench className="h-4 w-4 mt-1 text-sky-400 flex-shrink-0" />
    )}
    <div className="min-w-0">
      <p className="text-sm text-slate-100 truncate">{item.title}</p>
      <p className="text-xs text-slate-500 truncate">{item.excerpt || item.description}</p>
    </div>
    <span className="ml-auto text-[0.6rem] uppercase tracking-wider text-slate-500">
      {item.kind === "article" ? "Article" : "Project"}
    </span>
  </Link>
);

export const SiteSearch = ({ testid = "nav-search" }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!open || items) return;
    Promise.all([api.get("/articles"), api.get("/projects")]).then(([a, p]) =>
      setItems([
        ...a.data.map((x) => ({ ...x, kind: "article", href: `/articles/${x.id}` })),
        ...p.data.map((x) => ({ ...x, kind: "project", href: `/projects/${x.id}` })),
      ])
    );
  }, [open, items]);

  const results = q.trim() ? (items || []).filter((i) => matchesQuery(i, q)) : [];
  const close = () => setOpen(false);

  return (
    <>
      <button
        data-testid={testid}
        onClick={() => setOpen(true)}
        aria-label="Search"
        className="text-slate-400 hover:text-sky-400 transition-colors"
      >
        <Search className="h-4 w-4" />
      </button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQ(""); }}>
        <DialogContent data-testid="site-search-dialog" className="w-[calc(100%-2rem)] max-w-xl rounded-xl bg-[#0a111f] border-slate-800 text-slate-100 p-5 pt-12 top-[15%] translate-y-0">
          <DialogTitle className="sr-only">Search</DialogTitle>
          <DialogDescription className="sr-only">Search projects and articles</DialogDescription>
          <SearchInput value={q} onChange={setQ} placeholder="Search projects and articles…" testid="site-search-input" autoFocus />
          <div className="mt-3 max-h-[50vh] overflow-y-auto">
            {q.trim() && results.length === 0 && (
              <p data-testid="site-search-empty" className="px-4 py-6 text-sm text-slate-500 text-center">
                {items ? "No results found." : "Loading…"}
              </p>
            )}
            {results.map((item) => (
              <ResultRow key={`${item.kind}-${item.id}`} item={item} onPick={close} />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
